import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  Client,
  Dog,
  UpdateClientInput,
  CreateDogInput,
  UpdateDogInput,
} from "../lib/api";
import { DogEnrollments } from "../components/DogEnrollments";
import { DogVaccinations } from "../components/DogVaccinations";
import { ClientAppointments } from "../components/ClientAppointments";

interface EditClientFormProps {
  client: Client;
  onClose: () => void;
  onSuccess: () => void;
}

function EditClientForm({ client, onClose, onSuccess }: EditClientFormProps) {
  const [firstName, setFirstName] = useState(client.firstName);
  const [lastName, setLastName] = useState(client.lastName);
  const [email, setEmail] = useState(client.email);
  const [phone, setPhone] = useState(client.phone ?? "");
  const [notes, setNotes] = useState(client.notes ?? "");
  const [tagsInput, setTagsInput] = useState(client.tags?.join(", ") ?? "");
  const [isBanned, setIsBanned] = useState(client.isBanned);
  const [error, setError] = useState("");

  const updateMutation = useMutation({
    mutationFn: (data: UpdateClientInput) => api.clients.update(client.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to update client");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError("First name, last name, and email are required");
      return;
    }

    // Parse tags from comma-separated input
    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    updateMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone.trim() || null,
      notes: notes.trim() || null,
      tags: tags.length > 0 ? tags : null,
      isBanned,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Edit Client</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="editFirstName" className="block text-sm font-medium text-gray-700">
                First name
              </label>
              <input
                id="editFirstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
            <div>
              <label htmlFor="editLastName" className="block text-sm font-medium text-gray-700">
                Last name
              </label>
              <input
                id="editLastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
          </div>
          <div>
            <label htmlFor="editEmail" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              id="editEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>
          <div>
            <label htmlFor="editPhone" className="block text-sm font-medium text-gray-700">
              Phone
            </label>
            <input
              id="editPhone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>
          <div>
            <label htmlFor="editNotes" className="block text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              id="editNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>
          <div>
            <label htmlFor="editTags" className="block text-sm font-medium text-gray-700">
              Tags (comma-separated)
            </label>
            <input
              id="editTags"
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="VIP, Regular, New"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>
          <div className="flex items-center">
            <input
              id="editIsBanned"
              type="checkbox"
              checked={isBanned}
              onChange={(e) => setIsBanned(e.target.checked)}
              className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
            />
            <label htmlFor="editIsBanned" className="ml-2 block text-sm text-gray-700">
              Ban this client
            </label>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DogFormModalProps {
  clientId: string;
  dog?: Dog;
  onClose: () => void;
  onSuccess: () => void;
}

function DogFormModal({ clientId, dog, onClose, onSuccess }: DogFormModalProps) {
  const isEditing = !!dog;
  const [name, setName] = useState(dog?.name ?? "");
  const [breed, setBreed] = useState(dog?.breed ?? "");
  const [color, setColor] = useState(dog?.color ?? "");
  const [weight, setWeight] = useState(dog?.weight ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(dog?.dateOfBirth ?? "");
  const [gender, setGender] = useState<"male" | "female" | "unknown" | "">(dog?.gender ?? "");
  const [isNeutered, setIsNeutered] = useState<boolean | null>(dog?.isNeutered ?? null);
  const [notes, setNotes] = useState(dog?.notes ?? "");
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: CreateDogInput) => api.dogs.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create dog");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateDogInput) => api.dogs.update(dog!.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to update dog");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    const data = {
      name: name.trim(),
      breed: breed.trim() || null,
      color: color.trim() || null,
      weight: weight.trim() || null,
      dateOfBirth: dateOfBirth || null,
      gender: gender || null,
      isNeutered,
      notes: notes.trim() || null,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate({ ...data, clientId });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">{isEditing ? "Edit Dog" : "Add Dog"}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="dogName" className="block text-sm font-medium text-gray-700">
              Name *
            </label>
            <input
              id="dogName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="dogBreed" className="block text-sm font-medium text-gray-700">
                Breed
              </label>
              <input
                id="dogBreed"
                type="text"
                value={breed}
                onChange={(e) => setBreed(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
            </div>
            <div>
              <label htmlFor="dogColor" className="block text-sm font-medium text-gray-700">
                Color
              </label>
              <input
                id="dogColor"
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="dogWeight" className="block text-sm font-medium text-gray-700">
                Weight (lbs)
              </label>
              <input
                id="dogWeight"
                type="text"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
            </div>
            <div>
              <label htmlFor="dogDateOfBirth" className="block text-sm font-medium text-gray-700">
                Date of Birth
              </label>
              <input
                id="dogDateOfBirth"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="dogGender" className="block text-sm font-medium text-gray-700">
                Gender
              </label>
              <select
                id="dogGender"
                value={gender}
                onChange={(e) => setGender(e.target.value as "male" | "female" | "unknown" | "")}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              >
                <option value="">Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
            <div>
              <label htmlFor="dogIsNeutered" className="block text-sm font-medium text-gray-700">
                Neutered/Spayed
              </label>
              <select
                id="dogIsNeutered"
                value={isNeutered === null ? "" : isNeutered ? "yes" : "no"}
                onChange={(e) => {
                  if (e.target.value === "") setIsNeutered(null);
                  else setIsNeutered(e.target.value === "yes");
                }}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              >
                <option value="">Unknown</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="dogNotes" className="block text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              id="dogNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? "Saving..." : isEditing ? "Save Changes" : "Add Dog"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DogCardProps {
  dog: Dog;
  onEdit: (dog: Dog) => void;
  onDelete: (dog: Dog) => void;
}

function DogCard({ dog, onEdit, onDelete }: DogCardProps) {
  const age = dog.dateOfBirth
    ? Math.floor((Date.now() - new Date(dog.dateOfBirth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-2xl">
          {dog.profilePhotoUrl ? (
            <img src={dog.profilePhotoUrl} alt={dog.name} className="w-full h-full rounded-full object-cover" />
          ) : (
            <span aria-label="Dog icon">&#128054;</span>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <h3 className="font-semibold text-gray-900">{dog.name}</h3>
            <div className="flex gap-1">
              <button
                onClick={() => onEdit(dog)}
                className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                aria-label={`Edit ${dog.name}`}
              >
                Edit
              </button>
              <button
                onClick={() => onDelete(dog)}
                className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                aria-label={`Delete ${dog.name}`}
              >
                Delete
              </button>
            </div>
          </div>
          <div className="text-sm text-gray-500 space-y-1">
            {dog.breed && <p>{dog.breed}</p>}
            <div className="flex gap-3">
              {dog.gender && <span className="capitalize">{dog.gender}</span>}
              {age !== null && <span>{age} year{age !== 1 ? "s" : ""} old</span>}
              {dog.weight && <span>{dog.weight} lbs</span>}
            </div>
            {dog.isNeutered !== null && (
              <p>{dog.isNeutered ? "Neutered/Spayed" : "Not neutered/spayed"}</p>
            )}
          </div>
          {!dog.isActive && (
            <span className="inline-block mt-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
              Inactive
            </span>
          )}
        </div>
      </div>
      {(dog.notes || dog.behavioralNotes) && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-600">
          {dog.notes && <p>{dog.notes}</p>}
          {dog.behavioralNotes && (
            <p className="mt-1 text-amber-700">
              <strong>Behavioral:</strong> {dog.behavioralNotes}
            </p>
          )}
        </div>
      )}
      <DogVaccinations dogId={dog.id} dogName={dog.name} />
      <DogEnrollments dogId={dog.id} dogName={dog.name} />
    </div>
  );
}

export function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDogModal, setShowDogModal] = useState(false);
  const [editingDog, setEditingDog] = useState<Dog | null>(null);
  const [deletingDog, setDeletingDog] = useState<Dog | null>(null);

  const {
    data: client,
    isLoading: isLoadingClient,
    error: clientError,
  } = useQuery({
    queryKey: ["clients", clientId],
    queryFn: () => api.clients.get(clientId!),
    enabled: !!clientId,
  });

  const {
    data: dogsData,
    isLoading: isLoadingDogs,
    error: dogsError,
  } = useQuery({
    queryKey: ["dogs", "byClient", clientId],
    queryFn: () => api.dogs.byClient(clientId!),
    enabled: !!clientId,
  });
  const dogs = dogsData?.data;

  const deleteMutation = useMutation({
    mutationFn: () => api.clients.delete(clientId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      navigate("/clients");
    },
  });

  const deleteDogMutation = useMutation({
    mutationFn: (dogId: string) => api.dogs.delete(dogId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dogs", "byClient", clientId] });
      setDeletingDog(null);
    },
  });

  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["clients", clientId] });
  };

  const handleDogSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["dogs", "byClient", clientId] });
  };

  const handleEditDog = (dog: Dog) => {
    setEditingDog(dog);
    setShowDogModal(true);
  };

  const handleDeleteDog = (dog: Dog) => {
    setDeletingDog(dog);
  };

  const handleCloseDogModal = () => {
    setShowDogModal(false);
    setEditingDog(null);
  };

  if (!clientId) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Invalid client ID</p>
        </div>
      </div>
    );
  }

  if (isLoadingClient) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse text-gray-500">Loading client...</div>
      </div>
    );
  }

  if (clientError) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            {clientError instanceof ApiError ? clientError.message : "Failed to load client"}
          </p>
          <Link to="/clients" className="mt-2 inline-block text-amber-600 hover:text-amber-700">
            Back to clients
          </Link>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-8">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-gray-700">Client not found</p>
          <Link to="/clients" className="mt-2 inline-block text-amber-600 hover:text-amber-700">
            Back to clients
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <Link to="/clients" className="text-sm text-gray-500 hover:text-gray-700">
          &larr; Back to clients
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {client.firstName} {client.lastName}
              </h1>
              {client.isBanned && (
                <span className="inline-block mt-1 px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                  Banned {client.banReason && `- ${client.banReason}`}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-md hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
              Contact Information
            </h2>
            <dl className="space-y-2">
              <div>
                <dt className="text-sm text-gray-500">Email</dt>
                <dd className="text-gray-900">{client.email}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Phone</dt>
                <dd className="text-gray-900">{client.phone ?? "-"}</dd>
              </div>
              {client.address && (
                <div>
                  <dt className="text-sm text-gray-500">Address</dt>
                  <dd className="text-gray-900">
                    {[client.address.street, client.address.city, client.address.state, client.address.zipCode]
                      .filter(Boolean)
                      .join(", ") || "-"}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          <div>
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
              Statistics
            </h2>
            <dl className="space-y-2">
              <div>
                <dt className="text-sm text-gray-500">Total Appointments</dt>
                <dd className="text-gray-900">{client.totalAppointments}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Total Spent</dt>
                <dd className="text-gray-900">${parseFloat(client.totalSpent).toFixed(2)}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Last Appointment</dt>
                <dd className="text-gray-900">
                  {client.lastAppointmentAt
                    ? new Date(client.lastAppointmentAt).toLocaleDateString()
                    : "Never"}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Client Since</dt>
                <dd className="text-gray-900">{new Date(client.createdAt).toLocaleDateString()}</dd>
              </div>
            </dl>
          </div>
        </div>

        {client.notes && (
          <div className="px-6 pb-6">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Notes</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{client.notes}</p>
          </div>
        )}

        {client.tags && client.tags.length > 0 && (
          <div className="px-6 pb-6">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Tags</h2>
            <div className="flex flex-wrap gap-2">
              {client.tags.map((tag, i) => (
                <span key={i} className="px-2 py-1 text-sm bg-gray-100 text-gray-700 rounded">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Dogs ({isLoadingDogs ? "..." : dogs?.length ?? 0})
          </h2>
          <button
            onClick={() => setShowDogModal(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
          >
            Add Dog
          </button>
        </div>

        {isLoadingDogs ? (
          <div className="text-center py-8">
            <div className="animate-pulse text-gray-500">Loading dogs...</div>
          </div>
        ) : dogsError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">
              {dogsError instanceof ApiError ? dogsError.message : "Failed to load dogs"}
            </p>
          </div>
        ) : dogs?.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <p className="text-gray-500">No dogs registered for this client.</p>
            <button
              onClick={() => setShowDogModal(true)}
              className="mt-4 text-amber-600 hover:text-amber-700 font-medium"
            >
              Add the first dog
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dogs?.map((dog) => (
              <DogCard key={dog.id} dog={dog} onEdit={handleEditDog} onDelete={handleDeleteDog} />
            ))}
          </div>
        )}
      </div>


      <ClientAppointments clientId={clientId} />
      {showEditModal && client && (
        <EditClientForm client={client} onClose={() => setShowEditModal(false)} onSuccess={handleEditSuccess} />
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <h2 className="text-xl font-semibold mb-4">Delete Client?</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete {client.firstName} {client.lastName}? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDogModal && clientId && (
        <DogFormModal
          clientId={clientId}
          dog={editingDog ?? undefined}
          onClose={handleCloseDogModal}
          onSuccess={handleDogSuccess}
        />
      )}

      {deletingDog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <h2 className="text-xl font-semibold mb-4">Delete Dog?</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete {deletingDog.name}? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeletingDog(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteDogMutation.mutate(deletingDog.id)}
                disabled={deleteDogMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteDogMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
